import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

// Hover scale effect
export function HoverScale({ children, scale = 1.05, className = '' }: { children: ReactNode; scale?: number; className?: string }) {
  return (
    <motion.div
      whileHover={{ scale }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Magnetic hover effect
export function MagneticHover({ children, strength: _strength = 0.3, className = '' }: { children: ReactNode; strength?: number; className?: string }) {
  return (
    <motion.div
      whileHover="hover"
      className={`relative ${className}`}
    >
      <motion.div
        variants={{
          hover: {
            x: [-2, 2, -1, 1, 0],
            transition: { duration: 0.4 }
          }
        }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

// Glow pulse on hover
export function GlowPulse({ children, color = '#FFD700', className = '' }: { children: ReactNode; color?: string; className?: string }) {
  return (
    <motion.div
      className={`relative ${className}`}
      whileHover="hover"
      initial="initial"
    >
      <motion.div
        className="absolute inset-0 rounded-inherit blur-xl opacity-0"
        style={{ backgroundColor: color }}
        variants={{
          initial: { opacity: 0, scale: 0.8 },
          hover: { 
            opacity: 0.4, 
            scale: 1.2,
            transition: { duration: 0.3 }
          }
        }}
      />
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
}

// FIFA/Tron-style stat bar with neon glow
export function StatBar({ value, max = 100, label, delay = 0 }: { value: number; max?: number; label: string; delay?: number }) {
  const percentage = (value / max) * 100;
  
  // Color based on value
  const getColor = () => {
    if (percentage >= 80) return { bg: '#00d4aa', glow: '0 0 10px #00d4aa' }; // Teal for high
    if (percentage >= 60) return { bg: '#FFD700', glow: '0 0 10px #FFD700' }; // Gold for medium-high
    if (percentage >= 40) return { bg: '#00e5ff', glow: '0 0 10px #00e5ff' }; // Cyan for medium
    return { bg: '#7c3aed', glow: '0 0 10px #7c3aed' }; // Purple for low
  };
  
  const colorStyle = getColor();
  
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs">
        <span className="text-dark-400 uppercase tracking-wider font-medium">{label}</span>
        <motion.span 
          className="font-bold"
          style={{ color: colorStyle.bg }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: delay + 0.5 }}
        >
          {value}
        </motion.span>
      </div>
      <div className="h-2 bg-brand-blue/50 rounded-full overflow-hidden relative">
        {/* Background grid effect */}
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 4px, rgba(0,0,0,0.3) 4px, rgba(0,0,0,0.3) 5px)'
        }} />
        <motion.div
          className="h-full rounded-full relative"
          style={{ 
            backgroundColor: colorStyle.bg,
            boxShadow: colorStyle.glow,
          }}
          initial={{ width: 0 }}
          whileInView={{ width: `${percentage}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: 'easeOut', delay: delay }}
        >
          {/* Shine effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 2, repeat: Infinity, delay: delay + 1 }}
          />
        </motion.div>
      </div>
    </div>
  );
}

// Tron-style digital corner with neon glow
export function CardCorner({ position = 'top-left', color = 'teal' }: { position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'; color?: 'teal' | 'gold' | 'cyan' }) {
  const positionClasses = {
    'top-left': 'top-0 left-0',
    'top-right': 'top-0 right-0',
    'bottom-left': 'bottom-0 left-0',
    'bottom-right': 'bottom-0 right-0',
  };
  
  const colorClasses = {
    teal: 'from-fifa-teal to-transparent',
    gold: 'from-brand-yellow to-transparent',
    cyan: 'from-fifa-cyan to-transparent',
  };

  const isTop = position.includes('top');
  const isLeft = position.includes('left');

  return (
    <motion.div 
      className={`absolute ${positionClasses[position]} pointer-events-none`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Horizontal neon line */}
      <motion.div 
        className={`absolute ${isTop ? 'top-0' : 'bottom-0'} ${isLeft ? 'left-0' : 'right-0'} h-[2px] w-6 bg-gradient-to-r ${colorClasses[color]}`}
        style={{ 
          boxShadow: color === 'teal' ? '0 0 8px #00d4aa, 0 0 16px #00d4aa' : 
                     color === 'gold' ? '0 0 8px #FFD700, 0 0 16px #FFD700' :
                     '0 0 8px #00e5ff, 0 0 16px #00e5ff'
        }}
        animate={{ 
          opacity: [0.6, 1, 0.6],
        }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      {/* Vertical neon line */}
      <motion.div 
        className={`absolute ${isTop ? 'top-0' : 'bottom-0'} ${isLeft ? 'left-0' : 'right-0'} w-[2px] h-6 bg-gradient-to-b ${colorClasses[color]}`}
        style={{ 
          boxShadow: color === 'teal' ? '0 0 8px #00d4aa, 0 0 16px #00d4aa' : 
                     color === 'gold' ? '0 0 8px #FFD700, 0 0 16px #FFD700' :
                     '0 0 8px #00e5ff, 0 0 16px #00e5ff'
        }}
        animate={{ 
          opacity: [0.6, 1, 0.6],
        }}
        transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
      />
    </motion.div>
  );
}

// Tron-style animated border with scan effect
export function TronBorder({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`relative rounded-xl overflow-hidden ${className}`}>
      {/* Animated scan line */}
      <motion.div
        className="absolute inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-fifa-teal to-transparent"
        style={{ boxShadow: '0 0 10px #00d4aa, 0 0 20px #00d4aa' }}
        animate={{
          top: ['-2%', '102%'],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
        }}
      />
      {/* Border glow */}
      <div className="absolute inset-0 rounded-xl border border-fifa-teal/30" />
      <div className="relative">{children}</div>
    </div>
  );
}

// Digital pulse effect for cards
export function DigitalPulse({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <motion.div
      className={`relative ${className}`}
      whileHover="hover"
    >
      <motion.div
        className="absolute inset-0 rounded-xl bg-gradient-to-r from-fifa-teal/0 via-fifa-teal/20 to-fifa-teal/0"
        variants={{
          hover: {
            opacity: [0, 1, 0],
            transition: { duration: 0.8, repeat: Infinity }
          }
        }}
        initial={{ opacity: 0 }}
      />
      {children}
    </motion.div>
  );
}

// Animated border with Tron-style glow
export function AnimatedBorder({ children, className = '', color = 'mixed' }: { children: ReactNode; className?: string; color?: 'gold' | 'teal' | 'cyan' | 'mixed' }) {
  const gradients = {
    gold: 'linear-gradient(90deg, transparent, #FFD700, transparent)',
    teal: 'linear-gradient(90deg, transparent, #00d4aa, transparent)',
    cyan: 'linear-gradient(90deg, transparent, #00e5ff, transparent)',
    mixed: 'linear-gradient(90deg, transparent, #00d4aa, #FFD700, #00e5ff, transparent)',
  };
  
  return (
    <div className={`relative p-[1px] rounded-2xl overflow-hidden ${className}`}>
      <motion.div
        className="absolute inset-0"
        style={{
          background: gradients[color],
          filter: 'blur(1px)',
        }}
        animate={{
          x: ['-100%', '100%'],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear',
        }}
      />
      <div className="absolute inset-0 rounded-2xl border border-fifa-teal/20" />
      <div className="relative bg-brand-navy/90 backdrop-blur-sm rounded-2xl">
        {children}
      </div>
    </div>
  );
}

// Glitch text effect
export function GlitchText({ children, className = '' }: { children: string; className?: string }) {
  return (
    <motion.span 
      className={`relative inline-block ${className}`}
      whileHover="hover"
    >
      <motion.span
        className="absolute inset-0 text-brand-yellow opacity-0"
        style={{ clipPath: 'inset(0 0 50% 0)' }}
        variants={{
          hover: {
            opacity: 0.8,
            x: [-2, 2, -1, 1, 0],
            transition: { duration: 0.2 }
          }
        }}
      >
        {children}
      </motion.span>
      <motion.span
        className="absolute inset-0 text-accent-cyan opacity-0"
        style={{ clipPath: 'inset(50% 0 0 0)' }}
        variants={{
          hover: {
            opacity: 0.8,
            x: [2, -2, 1, -1, 0],
            transition: { duration: 0.2 }
          }
        }}
      >
        {children}
      </motion.span>
      {children}
    </motion.span>
  );
}

// Number counter with slot machine effect
export function SlotCounter({ value, className = '' }: { value: number; className?: string }) {
  const digits = value.toString().split('');
  
  return (
    <div className={`flex overflow-hidden ${className}`}>
      {digits.map((digit, index) => (
        <motion.span
          key={index}
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ 
            duration: 0.5, 
            delay: index * 0.1,
            type: 'spring',
            stiffness: 200
          }}
        >
          {digit}
        </motion.span>
      ))}
    </div>
  );
}

// Cursor follower dot
export function CursorDot() {
  return (
    <motion.div
      className="fixed w-4 h-4 rounded-full bg-brand-yellow pointer-events-none z-[9999] mix-blend-difference"
      animate={{
        scale: [1, 1.2, 1],
      }}
      transition={{
        duration: 1,
        repeat: Infinity,
      }}
      style={{
        left: 'var(--mouse-x)',
        top: 'var(--mouse-y)',
        transform: 'translate(-50%, -50%)',
      }}
    />
  );
}

